const axios = require("axios");

/* Connection test endpoint for the same-domain API base URL. It returns HTTP
   200 with the text "test" when the API is reachable on the domain the
   webstore itself is served from (e.g. https://emenu.sixsense.com/api). */
const CONNECTION_TEST_PATH = "/property/test";
const CONNECTION_TEST_TIMEOUT_MS = 10000;

const normalizeBaseUrl = (baseUrl) => (baseUrl || "").replace(/\/+$/, "");

const configuredBaseUrl = normalizeBaseUrl(process.env.REACT_APP_BASE_URL);

let cachedBaseUrl = null;
let resolvingBaseUrl = null;

/* When the webstore is served from e.g. https://emenu.sixsense.com but the
   configured API is https://emenu.okkami.com/api, build the same-domain
   equivalent https://emenu.sixsense.com/api (reusing the configured path).
   window.location.origin already includes the port when the URL has one. */
const getSameDomainBaseUrl = () => {
  if (typeof window === "undefined" || !configuredBaseUrl) {
    return null;
  }

  try {
    const { pathname } = new URL(configuredBaseUrl);
    return normalizeBaseUrl(`${window.location.origin}${pathname}`);
  } catch (e) {
    return null;
  }
};

/* Axios rejects on any non-2xx status, so the success handler receives a 2xx
   response. Any failure — timeout, DNS, 502 Bad Gateway — lands in the error
   handler and resolves to false, which makes the caller fall back to the
   configured cross-domain base URL. */
const isSameDomainApiReachable = (sameDomainBaseUrl) =>
  axios({
    method: "GET",
    url: `${sameDomainBaseUrl}${CONNECTION_TEST_PATH}`,
    timeout: CONNECTION_TEST_TIMEOUT_MS,
  }).then(
    (response) => response.status === 200,
    () => false
  );

const resolveBaseUrl = () => {
  const sameDomainBaseUrl = getSameDomainBaseUrl();

  if (sameDomainBaseUrl && sameDomainBaseUrl !== configuredBaseUrl) {
    return isSameDomainApiReachable(sameDomainBaseUrl).then(
      (reachable) => (reachable ? sameDomainBaseUrl : configuredBaseUrl)
    );
  }

  return Promise.resolve(configuredBaseUrl);
};

/* Resolve and cache the API base URL once. Prefers the same-domain API when the
   connection test passes, otherwise falls back to REACT_APP_BASE_URL.
   Never rejects — always resolves to a base URL string. */
const getBaseUrl = () => {
  if (cachedBaseUrl) {
    return Promise.resolve(cachedBaseUrl);
  }

  if (!resolvingBaseUrl) {
    resolvingBaseUrl = resolveBaseUrl().then((url) => {
      cachedBaseUrl = url;
      return url;
    });
  }

  return resolvingBaseUrl;
};

/* Kick off the connection test at startup so it runs once as early as possible
   and the first real API call doesn't wait for it. */
const init = () => {
  getBaseUrl();
};

module.exports = { getBaseUrl, init };